/**
 * Storey scope for a wall-length (stretch) edit.
 *
 * `computeStretch` clusters wall endpoints and polygon corners by X/Z alone,
 * and stacked storeys share X/Z (`placeStory` lifts Y only) — so without a
 * scope a ground-floor edit rewrote the floor above, invisibly, because the
 * canvas draws one floor at a time. Measured 2026-09-01 on 08E54F9E: one
 * 50 cm edit on a dining-room wall moved 11 upstairs walls, lengthened 3 of
 * them and reshaped 2 upstairs rooms, with the preview showing none of it.
 *
 * The rule: an edit is scoped to the edited wall's own storey. Another storey
 * joins only through a SHARED structure wall — one record that rooms on both
 * floors sit on (`buildWallStoryLookup`: a double-height stairwell or loft
 * wall) — and only when that wall would move. A recruited storey gets the
 * identical cut: everything past the boundary translates rigidly, walls
 * crossing it stretch. Stacked walls on separate records are NOT shared;
 * lengthening the ground floor leaves the floor above where it was, and the
 * user edits it separately.
 *
 * Pure. Takes the in-app structure shape (`walls` + `rooms` at the top level)
 * and returns a narrowed copy for `computeStretch` to run over, plus the scope
 * for the panel to announce. The plan names only in-scope ids, and applying
 * it to the FULL structure (preview, persist, the lock re-measure) is what
 * leaves the out-of-scope floors untouched.
 */
import { buildWallStoryLookup } from './storyOptions';

const matchesAny = (el, stories) =>
  !Number.isFinite(el?.story) || stories.has(el.story);

const anyIn = (own, stories) => {
  for (const s of own) if (stories.has(s)) return true;
  return false;
};

/** The storey of the room whose wall carries `roomWallId`, or null. */
export function storyOfRoomWall(structureData, roomWallId) {
  for (const room of structureData?.rooms || []) {
    if ((room.walls || []).some((w) => w?.id === roomWallId)) {
      return Number.isFinite(room.story) ? room.story : null;
    }
  }
  return null;
}

/**
 * Narrow the structure to `stories`. Structure walls go through the lookup;
 * structure-level children follow their host wall when it is a structure
 * wall and their own stamp otherwise; exterior walls follow their structure
 * wall. Keys absent on the input stay absent.
 */
export function scopeStructureToStories(structureData, stories, lookup) {
  const keep = (el) => matchesAny(el, stories);
  const keepWall = (wall) => {
    const own = lookup.get(wall?.id);
    return own ? anyIn(own, stories) : keep(wall);
  };
  const allWalls = structureData.walls || [];
  const walls = allWalls.filter(keepWall);
  const allWallIds = new Set(allWalls.map((w) => w?.id));
  const keptWallIds = new Set(walls.map((w) => w?.id));
  const keepChild = (el) => {
    const parentId = el?.parentWallIds?.[0];
    return parentId && allWallIds.has(parentId)
      ? keptWallIds.has(parentId)
      : keep(el);
  };
  const scoped = {
    ...structureData,
    walls,
    rooms: (structureData.rooms || []).filter(keep),
  };
  for (const key of ['doors', 'windows', 'openings', 'objects']) {
    if (Array.isArray(structureData[key])) {
      scoped[key] = structureData[key].filter(keepChild);
    }
  }
  if (Array.isArray(structureData.floors)) {
    scoped.floors = structureData.floors.filter(keep);
  }
  if (Array.isArray(structureData.exterior?.walls)) {
    scoped.exterior = {
      ...structureData.exterior,
      walls: structureData.exterior.walls.filter((ext) => {
        const own = lookup.get(ext?.alignment?.structureWallId);
        return own ? anyIn(own, stories) : keep(ext);
      }),
    };
  }
  return scoped;
}

/**
 * The scope with no recruitment — the edited wall's own storey, falling back
 * to the selected floor. What the boundary rectangle, the forbidden ranges
 * and the lock zones measure against; identity when there is no storey.
 */
export function scopeStructureForMeasurement(
  structureData,
  measurement,
  story
) {
  if (!structureData) return structureData;
  const base = storyOfRoomWall(structureData, measurement?.wallId) ?? story;
  if (base == null) return structureData;
  const lookup = buildWallStoryLookup(structureData.rooms, structureData.walls);
  return scopeStructureToStories(structureData, new Set([base]), lookup);
}

/**
 * Every storey the edit could reach: its own, plus each one a shared wall
 * would recruit if it moved (`resolveStretchStoryScope` with every wall
 * moving). Where the box's cut may not fall is measured against these
 * (`structuralCutRanges`): a floor the edit recruits needs its walls kept
 * whole, which the edited floor alone did not do (`FB5B0707`), and a floor it
 * can never reach has no say — measured against every floor, two
 * ground-floor walls under Pittsburgh Dev / Scan 2's upstairs closet sent a
 * typed edit's cut onto a slanted wall, and the edit was refused.
 */
export function reachableStructureForMeasurement(
  structureData,
  measurement,
  story
) {
  if (!structureData) return structureData;
  return resolveStretchStoryScope(structureData, {
    story,
    editedRoomWallId: measurement?.wallId,
    isWallMoving: () => true,
  }).structureData;
}

/**
 * Resolve the storeys a stretch reaches and narrow the structure to them.
 *
 * @param {Object} structureData
 * @param {Object} opts
 * @param {number|null} opts.story  the selected floor — the base only when
 *        the edited wall's room carries no storey.
 * @param {string} opts.editedRoomWallId
 * @param {(wall: Object) => boolean} opts.isWallMoving  true when either
 *        endpoint of the wall lies inside the translate region.
 * @returns {{ structureData: Object, lookup: Map|null, scope: null | {
 *   story: number, stories: number[],
 *   recruited: Array<{ story: number, viaWallIds: string[] }> } }}
 */
export function resolveStretchStoryScope(
  structureData,
  { story, editedRoomWallId, isWallMoving }
) {
  const base = storyOfRoomWall(structureData, editedRoomWallId) ?? story;
  if (base == null) return { structureData, lookup: null, scope: null };
  const walls = structureData.walls || [];
  const lookup = buildWallStoryLookup(structureData.rooms, walls);
  const stories = new Set([base]);
  const recruited = new Map();
  // Fixpoint: a storey recruited through one shared wall can recruit the next
  // (three-storey structures exist in the corpus).
  let grew = true;
  while (grew) {
    grew = false;
    for (const wall of walls) {
      const own = lookup.get(wall?.id);
      if (!own || own.size < 2 || !anyIn(own, stories)) continue;
      const others = [...own].filter((s) => !stories.has(s));
      if (others.length === 0 || !isWallMoving(wall)) continue;
      for (const s of others) {
        stories.add(s);
        if (!recruited.has(s)) recruited.set(s, []);
        recruited.get(s).push(wall.id);
        grew = true;
      }
    }
  }
  return {
    structureData: scopeStructureToStories(structureData, stories, lookup),
    lookup,
    scope: {
      story: base,
      stories: [...stories].sort((a, b) => a - b),
      recruited: [...recruited]
        .map(([s, viaWallIds]) => ({ story: s, viaWallIds }))
        .sort((a, b) => a.story - b.story),
    },
  };
}

/**
 * Per-storey counts for the panel: walls that shift / resize, rooms that
 * translate / reshape. A shared wall counts on every floor it belongs to — it
 * changes on each. Unstamped rooms count on the base storey.
 */
export function countStoryScope(
  scope,
  lookup,
  { affectedStructureWalls, roomClassification, rooms }
) {
  const byStory = {};
  for (const s of scope.stories) {
    byStory[s] = { shift: 0, resize: 0, roomsTranslate: 0, roomsReshape: 0 };
  }
  for (const entry of affectedStructureWalls || []) {
    const own = lookup?.get(entry.wallId) || [scope.story];
    for (const s of own) {
      const b = byStory[s];
      if (!b) continue;
      if (entry.kind === 'translate') b.shift += 1;
      else if (entry.kind === 'stretch') b.resize += 1;
    }
  }
  const roomStory = new Map(
    (rooms || []).map((r) => [
      r.id,
      Number.isFinite(r.story) ? r.story : scope.story,
    ])
  );
  for (const id of roomClassification?.translate || []) {
    const b = byStory[roomStory.get(id)];
    if (b) b.roomsTranslate += 1;
  }
  for (const id of roomClassification?.reshape || []) {
    const b = byStory[roomStory.get(id)];
    if (b) b.roomsReshape += 1;
  }
  return byStory;
}
